import type { SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input, output } from '@angular/core';

import type { Candidate } from '@recruitee/candidate-types';
import { TranslatePipe } from '@recruitee/i18n';
import type { InterviewResult, ResultGroup } from '@recruitee/interview-results-types';
import { OfferKind } from '@recruitee/offers-types';
import type { Placement } from '@recruitee/placements-types';
import {
  BoxComponent,
  IconComponent,
  RtTooltip,
  TimeAgoAsyncPipe,
} from '@recruitee/user-interface';

import { InterviewResultSummaryOverallHiddenEvaluationsGaugeChartComponent } from './interview-result-summary-overall-hidden-evaluations-gauge-chart/interview-result-summary-overall-hidden-evaluations-gauge-chart.component';
import { InterviewResultSummaryOverallItemComponent } from './interview-result-summary-overall-item/interview-result-summary-overall-item.component';
import { InterviewResultsSummaryChartComponent } from '../../../../interview-results-summary-chart/interview-results-summary-chart.component';

@Component({
  selector: 'rt-interview-result-summary-overall',
  templateUrl: 'interview-result-summary-overall.component.html',
  styleUrls: ['./interview-result-summary-overall.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InterviewResultsSummaryChartComponent,
    InterviewResultSummaryOverallHiddenEvaluationsGaugeChartComponent,
    BoxComponent,
    IconComponent,
    RtTooltip,
    InterviewResultSummaryOverallItemComponent,
    TimeAgoAsyncPipe,
    TranslatePipe,
  ],
})
export class InterviewResultSummaryOverallComponent {
  @Input() public candidate: Candidate;
  @Input() public groups: ResultGroup[];
  @Input() public offerId?: number;
  @Input() public lastUpdate: string | null;
  @Input() public currentUserAvailablePlacements: Placement[] = [];
  @Input() public hasAccessToAllJobOffers?: boolean;
  @Input() public results: InterviewResult[] = [];
  @Input() public hasNewCandidateProfile: boolean = true;

  public summaryResultCounterClick = output();

  public uniqAdmins: number;
  public selectedPlacement?: Placement;
  public ratingVisible: boolean = true;
  public userEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage =
    false;

  public ngOnInit(): void {
    this.setUniqAdmins();
    this.selectPlacement();
    this.setRatingVisibility();
    this.setUserEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.offerId || changes.lastUpdate?.currentValue !== changes.lastUpdate?.previousValue) {
      this.setUniqAdmins();
      this.selectPlacement();
      this.setRatingVisibility();
      this.setUserEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage();
    }

    if (changes.candidate) {
      this.setUserEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage();
    }
  }

  public hasMultiplePlacements(): boolean {
    return this.candidate.placements.length > 1;
  }

  public trackByGroup(index: number, group: ResultGroup): string {
    return JSON.stringify(group);
  }

  public get positiveRatings(): number | null {
    if (this.selectedPlacement) {
      return this.selectedPlacement.ratingVisible ? this.selectedPlacement.positiveRatings! : null;
    }

    return this.candidate.ratingVisible ? this.candidate.positiveRatings : null;
  }

  public get evaluationTranslationKey(): string {
    if (!this.offerId) {
      return 'app.interview.average_evaluation_score';
    } else {
      return this.selectedPlacement!.offer?.kind === OfferKind.Job
        ? 'app.interview.evaluation_score'
        : 'app.interview.talent_pool_fit_score';
    }
  }

  private setRatingVisibility(): void {
    if (this.selectedPlacement) {
      this.ratingVisible = !!this.selectedPlacement.ratingVisible;
    } else {
      this.ratingVisible = this.candidate.ratingVisible;
    }
  }

  private selectPlacement() {
    this.selectedPlacement = this.offerId
      ? this.candidate.placements.find(placement => placement.offerId === this.offerId)
      : undefined;
  }

  private setUniqAdmins() {
    this.uniqAdmins = new Set(
      this.results.map(result => result.admin?.id || result.guest?.id),
    ).size;
  }

  private setUserEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage(): void {
    if (this.selectedPlacement) {
      this.userEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage =
        this.selectedPlacement.stage &&
        this.selectedPlacement.stage.fairEvaluationsEnabled &&
        !!this.selectedPlacement.ratingVisible;
      return;
    }
    this.userEvaluatedCandidateInAtLeastOnePlacementWithFariEvaluationsEnabledAtCurrentStage =
      this.candidate.placements.some(
        placement =>
          placement.stage && placement.stage.fairEvaluationsEnabled && placement.ratingVisible,
      );
  }
}
